<script setup lang="ts">
import type { BreakdownAlternative } from '../../../composables/useQuestionBreakdown';
import useLocale from '../../../../composables/use-locale';

defineProps<{
  alternative: BreakdownAlternative;
  expanded: boolean;
}>();

const emit = defineEmits<{
  (e: 'toggle'): void;
}>();

const { t } = useLocale();
</script>

<template>
  <div class="pa-3 mb-2 rounded-lg bg-surface-container-lowest">
    <div class="d-flex align-center flex-wrap ga-2">
      <v-avatar
        size="26"
        :color="alternative.isCorrect ? 'green' : 'surface-container-high'"
        class="text-caption font-weight-medium"
      >
        {{ alternative.letter }}
      </v-avatar>

      <span class="text-body-2 flex-grow-1">{{ alternative.text }}</span>

      <v-chip v-if="alternative.isCorrect" size="x-small" color="green" variant="flat">
        {{ t('results.answer_recap.correct_answer_tag') }}
      </v-chip>

      <span class="text-caption on-surface-medium">
        {{ t('results.answer_recap.players_pct', { count: alternative.count, pct: alternative.pct }) }}
      </span>
    </div>

    <v-progress-linear
      :model-value="alternative.pct"
      :color="alternative.isCorrect ? 'green' : 'highlighterred'"
      bg-color="surface-container-high"
      height="8"
      rounded
      class="my-2"
    />

    <!-- Collapsed by default every time: this is what keeps names off a projector. -->
    <v-btn
      v-if="alternative.count > 0"
      size="x-small"
      variant="text"
      color="primary"
      class="text-none px-0"
      :data-testid="`quizter-breakdown-toggle-${alternative.letter}`"
      @click="emit('toggle')"
    >
      {{
        expanded
          ? t('results.answer_recap.hide_players')
          : t('results.answer_recap.show_players', { count: alternative.count })
      }}
    </v-btn>
    <span v-else class="text-caption on-surface-low">{{ t('results.answer_recap.no_players') }}</span>

    <div
      v-if="expanded"
      class="d-flex flex-wrap ga-2 mt-2"
      :data-testid="`quizter-breakdown-players-${alternative.letter}`"
    >
      <v-chip v-for="player in alternative.players" :key="player.id" size="small" variant="tonal">
        <v-avatar v-if="player.avatar" :image="player.avatar" size="18" start />
        {{ player.label }}
      </v-chip>
    </div>
  </div>
</template>
